import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import can from 'ember-can/helpers/can';
import set from 'ember-set-helper/helpers/set';
import { and, eq } from 'ember-truth-helpers';
import {
  HdsPageHeader,
  HdsBadge,
  HdsDropdown,
  HdsAlert,
  HdsTextDisplay,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Flex,
  CoreModalConfirm,
  Typography,
} from 'core/components';
import { boxPadding } from 'core/modifiers';
import IamPolicy from 'core/utils/iam-policy';
import TerraformPermissions from 'hcp/components/hcp/alert/terraform-permissions';
import SummaryCard from 'hcp/components/iam/groups/summary-card';
import RolesTable from 'hcp/components/iam/groups/roles-table';
import RoleDefinitions from 'manage-access/components/manage-access/flyout/role-definitions';

/**
 * Component: PageGroupsProjectDetail
 *
 * Displays the details of a project-level group. If the group contains members, they are listed in a table.
 *
 * @argument {Object} currentRolesLocation - Represents the current location of roles, with link and roles. Required for the RoleDefinitionsFlyout.
 * @argument {Object} currentScopeLink - Represents the current scope, with properties type, name, and id. Required for the RoleDefinitionsFlyout.
 * @argument {Array<Object>} group - The group selected for the detail page to present.
 * @argument {Boolean} isProjectContext - Indicates whether the current scope is at organization or project level.
 * @argument {Array<Object>} members - Members within a group.
 * @argument {Array<Object>} project - The project object.
 * @argument {String} role - The group's role.
 *
 * @example
 * <Page::Groups::Project::Detail
 *   @currentRolesLocation={{@currentRolesLocation}}
 *   @currentScopeLink={{@currentScopeLink}}
 *   @group={{@model.group}}
 *   @isProjectContext={{@model.isProjectContext}}
 *   @members={{@model.members}}
 *   @project={{@model.project}}
 *   @role={{@model.role}}
 * />
 */
export default class PageGroupsProjectDetail extends Component {
  @service api;
  @service flashMessages;
  @service intl;
  @service router;

  @tracked showRemoveModal = false;
  @tracked groupToRemove = '';
  @tracked showRoleDefinitionsFlyout = false;

  /**
   * Removes a group binding from a policy and attempts to update the policy.
   * If the policy has been updated concurrently, it will retry up to maxRetries.
   */
  remove = task(async () => {
    const projectId = this.args.project.id;
    const resourceId = this.groupToRemove.resourceId;
    const maxRetries = 3;

    const { updated } = await this.removeGroupFromPolicy(
      projectId,
      resourceId,
      maxRetries
    );

    if (updated) {
      this.flashMessages.success(
        this.intl.t(
          `components.page.access-control.groups.remove.modal.success.title`
        ),
        {
          content: this.intl.t(
            `components.page.access-control.groups.remove.modal.success.content`,
            {
              groupName: this.args.group.displayName,
              projectName: this.args.project.name,
              htmlSafe: true,
            }
          ),
        }
      );
      this.refreshRoute();
      return this.router.transitionTo('cloud.access-control.groups.list');
    } else {
      this.flashMessages.error(
        this.intl.t(
          `components.page.access-control.groups.remove.modal.error.title`
        ),
        {
          content: this.intl.t(
            `components.page.access-control.groups.remove.modal.error.content`,
            {
              groupName: this.args.group.displayName,
              projectName: this.args.project.name,
              htmlSafe: true,
            }
          ),
        }
      );
    }
  });

  async removeGroupFromPolicy(projectId, resourceId, maxRetries) {
    for (let i = 0; i < maxRetries; i++) {
      try {
        // Get the latest policy in order to get the latest etag to protect
        // from concurrent updates.
        const { policy } =
          await this.api.resourceManager.project.projectServiceGetIamPolicy(
            projectId
          );

        if (!policy) {
          throw new Error();
        }

        // Given the latest policy, attempt to remove the group
        const policyToUpdate = new IamPolicy(policy);
        policyToUpdate.removeMemberById(resourceId);

        const updatedPolicy = policyToUpdate.get();
        const policyPayload = {
          policy: {
            ...updatedPolicy,
          },
        };

        await this.api.resourceManager.project.projectServiceSetIamPolicy(
          projectId,
          policyPayload
        );

        return {
          updated: true,
        };
      } catch (e) {
        // A conflict indicates a concurrent update.
        // We may just need try again.
        if (e.status === 409) {
          continue;
        }

        return {
          updated: false,
        };
      }
    }

    return {
      updated: false,
    };
  }

  confirmRemoveGroup = task(async (group) => {
    this.groupToRemove = group;
    this.showRemoveModal = true;
  });

  resetRemoveGroup = () => {
    this.showRemoveModal = false;
  };

  refreshRoute = () => {
    const listRoute = 'cloud.access-control.groups.list';
    let route = getOwner(this).lookup(`route:${listRoute}`);
    return route.refresh();
  };

  <template>
    {{#let
      (and (eq @scimEnabled true) (eq @group.scimSynchronized true))
      as |scimManaged|
    }}
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.IconTile @icon="users" />
            <PH.Title>
              {{@group.displayName}}
              {{#if (eq scimManaged true)}}
                <HdsBadge
                  @text={{t
                    "components.page.access-control.groups.detail.title.scim-synchronized"
                  }}
                  data-test-groups-project-detail-scim-synchronized-badge
                />
              {{/if}}
            </PH.Title>
            <PH.Actions>
              {{#if (can "delete groups")}}
                <HdsDropdown
                  @listPosition="bottom-right"
                  data-test-more-menu
                  as |DD|
                >
                  <DD.ToggleButton
                    @color="secondary"
                    @text={{t "components.form.manage"}}
                    data-test-groups-project-manage
                  />
                  <DD.Interactive
                    @icon="edit"
                    @route="cloud.access-control.groups.detail.edit"
                    @model={{@group.resourceName}}
                    @text={{t
                      "components.page.access-control.groups.list.actions.edit-role"
                    }}
                    data-test-groups-project-edit
                  />
                  <DD.Interactive
                    @color="critical"
                    @icon="minus-circle"
                    @text={{t
                      "components.page.access-control.groups.detail.members.actions.remove-group-members"
                    }}
                    {{on "click" (fn this.confirmRemoveGroup.perform @group)}}
                    data-test-groups-project-remove
                  />
                </HdsDropdown>
              {{/if}}
            </PH.Actions>
            <PH.Description>
              <Flex @direction="row" @gap="xs">
                {{@group.description}}
                {{#if (eq scimManaged true)}}
                  <HdsAlert
                    @type="compact"
                    data-test-groups-project-detail-scim-description
                    as |A|
                  >
                    <A.Description>
                      {{t
                        "components.page.access-control.groups.detail.scim-description.project"
                      }}
                    </A.Description>
                  </HdsAlert>
                {{/if}}
              </Flex>
            </PH.Description>
          </HdsPageHeader>
        </:header>
        <:alert>
          <TerraformPermissions as |Controls|>
            <Controls.GoToTerraform />
          </TerraformPermissions>
          <SummaryCard
            @group={{@group}}
            @scimEnabled={{@scimEnabled}}
            @ssoEnabled={{@ssoEnabled}}
            @truncatedResourceName={{@truncatedResourceName}}
          />
        </:alert>
        <:body>
          <Flex @direction="column" @gap="sm">
            <HdsTextDisplay @tag="h2" @size="400">
              {{t "components.page.access-control.groups.detail.role.title"}}
            </HdsTextDisplay>
            <Flex @alignItems="center" @direction="row" @justifyContent="end">
              <HdsButton
                @color="tertiary"
                @icon="help"
                @iconPosition="leading"
                @isInline={{true}}
                @text={{t
                  "components.page.access-control.groups.detail.view-role-definitions"
                }}
                {{on "click" (set this "showRoleDefinitionsFlyout" true)}}
                data-test-roles-chart-toggle-button
              />
            </Flex>
            <RolesTable
              @roleLocations={{@roleLocations}}
              @scope={{@project.name}}
              @type="hashicorp.resource-manager.project"
            />
          </Flex>
        </:body>
      </CoreLayoutMain>
    {{/let}}
    {{#if this.showRemoveModal}}
      <CoreModalConfirm
        @confirmButtonText={{t
          "components.page.access-control.groups.remove.modal.tagline"
        }}
        @confirmHelp={{t
          "components.page.access-control.groups.remove.modal.help-text"
        }}
        @confirmText={{t
          "components.page.access-control.groups.remove.modal.remove"
        }}
        @headerIcon="alert-triangle"
        @headerTagline={{t
          "components.page.access-control.groups.remove.modal.tagline"
        }}
        @headerText={{t
          "components.page.access-control.groups.remove.modal.header"
          groupName=@group.displayName
          htmlSafe=true
        }}
        @onCancel={{this.resetRemoveGroup}}
        @onSubmit={{this.remove}}
      >
        <:body>
          <Typography
            @variant="body1"
            class="hds-typography-body-200 hds-font-weight-semibold hds-foreground-primary"
          >
            {{t "components.page.access-control.groups.remove.modal.title"}}
          </Typography>
          <Typography
            @variant="body1"
            class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
            {{boxPadding "2xs 0 sm 0"}}
          >
            {{t
              "components.page.access-control.groups.remove.modal.body"
              groupName=@group.displayName
              projectName=@project.name
              htmlSafe=true
            }}
          </Typography>
        </:body>
      </CoreModalConfirm>
    {{/if}}
    {{#if this.showRoleDefinitionsFlyout}}
      <RoleDefinitions
        @rolesLocation={{@currentRolesLocation}}
        @currentScopeLink={{@currentScopeLink}}
        @onClose={{set this "showRoleDefinitionsFlyout" false}}
      />
    {{/if}}
  </template>
}
